// src/pages/product/PartnerProductDetail.tsx

import { useEffect, useState, type ReactNode } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Button,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { ArrowBackIosNew, ContentCopy } from '@mui/icons-material';
import { useTheme, type Theme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';

import {
  PartnerProductResponse,
  ProductSaleType,
  ProductType,
} from '@apis/product';
import { usePartnerProductDetail } from '@queries/products/usePartnerProductDetail';

import { ProductSaleTypeDisplay } from '@components/product/ProductSaleTypeDisplay';
import {
  formatRefreshCycle,
  getRequiredInfoItems,
  ProductDataCapacityDisplay,
  ProductDataTypeDisplay,
  ProductFupDisplay,
  ProductTypeDisplay,
  ProductValidityDisplay,
  ProductVoiceDisplay,
} from '@components/product/PartnerProductSpecDisplays';

import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

type Country = {
  countryId: number;
  englishName: string;
};

const cardSx = (theme: Theme) => ({
  borderRadius: 2,
  border: `1px solid ${theme.palette.divider}`,
  mb: 3,
});

const generalGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, minmax(0, 1fr))',
    xl: 'repeat(3, minmax(0, 1fr))',
  },
  gap: 2.5,
} as const;

const specsGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
    xl: 'repeat(6, minmax(0, 1fr))',
  },
  gap: 2.5,
} as const;

const DetailField = ({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) => (
  <Box
    sx={{
      minWidth: 0,
      gridColumn: wide
        ? '1 / -1'
        : undefined,
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', fontWeight: 800 }}
    >
      {label}
    </Typography>
    <Box mt={0.75}>{children}</Box>
  </Box>
);

/**
 * PartnerProductDetail
 *
 * Partner-facing read-only view of a single product.
 *
 * IMPORTANT (Header standard):
 * - Main title/description/breadcrumbs are defined via route handle.pageHeader in App.tsx.
 * - Product name/code are shown in the General card to avoid duplicating
 *   product identifiers in the global AppPageHeader.
 * - This page does NOT inject header slots or actions.
 *
 * Responsibilities:
 * - Fetch partner product detail
 * - Show:
 *   - Basic info (code / name / description)
 *   - Specification (validity, data, FUP, refresh, voice, coverage)
 *   - Order / product type and required customer fields
 */
export const PartnerProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const systemAlert = useSystemAlert();
  const theme = useTheme();
  const isMobileCoverage = useMediaQuery(theme.breakpoints.down('sm'));
  const [isCoverageExpanded, setCoverageExpanded] = useState(false);

  const { data } = usePartnerProductDetail(Number(productId));

  // Fallback when data is not yet loaded (keeps UI stable)
  const productDetail: PartnerProductResponse = data?.data[0] ?? {
    productId: 0,
    productCode: '',
    productSaleType: ProductSaleType.REAL_TIME,
    productType: ProductType.ESIM,
    productName: '',
    description: '',
    specification: {
      dayCount: 0,
      isUnlimitedData: true,
      isVoiceCallSupported: false,
      dataCapacity: {
        quantity: 0,
        unit: 'GB',
        refreshPeriodUnit: 'NEVER',
        refreshPeriodValue: 0,
      },
      isFup: false,
    },
    productCoverages: [],
    requiredData: {
      requiredFields: [],
    },
  };

  const spec = productDetail.specification;
  const requiredFields = productDetail.requiredData?.requiredFields ?? [];

  const coverages: Country[] =
    productDetail.productCoverages?.map((c) => ({
      countryId: c.country.countryId,
      englishName: c.country.englishName,
    })) ?? [];

  const sortedCoverages = [...coverages].sort((a, b) =>
    a.englishName.localeCompare(b.englishName),
  );
  const coveragePreviewCount = isMobileCoverage ? 6 : 12;
  const visibleCoverages = isCoverageExpanded
    ? sortedCoverages
    : sortedCoverages.slice(0, coveragePreviewCount);
  const hiddenCoverageCount = Math.max(
    sortedCoverages.length - visibleCoverages.length,
    0,
  );
  const hasHiddenCoverages = hiddenCoverageCount > 0;

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    setCoverageExpanded(false);
  }, [productDetail.productId]);

  const refreshText = formatRefreshCycle(spec);
  const requiredInfoItems = getRequiredInfoItems(
    requiredFields,
    productDetail.productSaleType,
  );

  const handleCopyProductCode = async () => {
    const text = productDetail.productCode.trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      systemAlert.success('Product code copied.', { durationMs: 1600 });
    } catch {
      systemAlert.error('Copy failed.');
    }
  };

  return (
    <Box width="100%">
      <Card elevation={0} sx={cardSx}>
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              General
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Basic product details such as code, type and order method.
            </Typography>
          }
        />
        <CardContent>
          <Box sx={generalGridSx}>
            <DetailField label="Product Code">
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 800, wordBreak: 'break-all' }}>
                  {productDetail.productCode || '-'}
                </Typography>
                {productDetail.productCode && (
                  <Tooltip title="Copy product code" arrow>
                    <IconButton
                      size="small"
                      aria-label="Copy product code"
                      onClick={handleCopyProductCode}
                      sx={{
                        width: 24,
                        height: 24,
                        color: 'text.secondary',
                        flexShrink: 0,
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      <ContentCopy sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </DetailField>

            <DetailField label="Product Type">
              <ProductTypeDisplay productType={productDetail.productType} />
            </DetailField>

            <DetailField label="Order Type">
              <ProductSaleTypeDisplay saleType={productDetail.productSaleType} />
            </DetailField>

            <DetailField label="Product Name" wide>
              <Typography variant="body1" sx={{ fontWeight: 800 }}>
                {productDetail.productName || '-'}
              </Typography>
            </DetailField>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={cardSx}>
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              Description
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Product usage notes and supplier-provided details.
            </Typography>
          }
        />
        <CardContent>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.65,
              maxWidth: 980,
            }}
          >
            {productDetail.description || '-'}
          </Typography>
        </CardContent>
      </Card>

      <Card elevation={0} sx={cardSx}>
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              Plan specs
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Data, validity, FUP, voice support and refresh cycle.
            </Typography>
          }
        />
        <CardContent>
          <Box sx={specsGridSx}>
            <DetailField label="Data Type">
              <ProductDataTypeDisplay specification={spec} />
            </DetailField>

            <DetailField label="Data">
              <ProductDataCapacityDisplay specification={spec} />
            </DetailField>

            <DetailField label="FUP">
              <ProductFupDisplay specification={spec} />
            </DetailField>

            <DetailField label="Validity">
              <ProductValidityDisplay dayCount={spec.dayCount} />
            </DetailField>

            <DetailField label="Voice">
              <ProductVoiceDisplay specification={spec} />
            </DetailField>

            <DetailField label="Refresh">
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {refreshText}
              </Typography>
            </DetailField>

            <DetailField label="Coverage" wide>
              {sortedCoverages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No countries configured.
                </Typography>
              ) : (
                <Stack spacing={1.25} alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary">
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>
                      {sortedCoverages.length.toLocaleString()}
                    </Box>{' '}
                    {sortedCoverages.length === 1 ? 'country' : 'countries'}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      columnGap: 1,
                      rowGap: 1.2,
                      maxWidth: '100%',
                    }}
                  >
                    {visibleCoverages.map((c) => (
                      <Chip
                        key={c.countryId}
                        label={c.englishName}
                        size="small"
                        sx={{
                          maxWidth: { xs: '100%', sm: 220 },
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    ))}
                    {hasHiddenCoverages && (
                      <Chip
                        label={`+${hiddenCoverageCount.toLocaleString()} more`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => setCoverageExpanded(true)}
                        sx={{ cursor: 'pointer', fontWeight: 800 }}
                      />
                    )}
                  </Box>

                  {isCoverageExpanded && sortedCoverages.length > coveragePreviewCount && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setCoverageExpanded(false)}
                      sx={{
                        minWidth: 0,
                        px: 0,
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      Show less
                    </Button>
                  )}
                </Stack>
              )}
            </DetailField>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={cardSx}>
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              Required
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Information that must be collected before placing an order.
            </Typography>
          }
        />
        <CardContent>
          <Stack direction="row" flexWrap="wrap" gap={1.25}>
            {requiredInfoItems.map((item) => (
              <Tooltip key={item.key} title={item.tooltip} arrow>
                <Box
                  component="span"
                  sx={(theme) => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.8,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    color: item.required ? item.color : 'text.disabled',
                    bgcolor: item.required ? 'action.hover' : 'transparent',
                    opacity: item.required ? 1 : 0.42,
                  })}
                  aria-label={item.label}
                >
                  {item.icon}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: 'inherit',
                      fontWeight: item.required ? 800 : 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Bottom: keep only the existing back action (non-header UI) --------- */}
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIosNew fontSize="small" />}
        >
          Back
        </Button>
      </Box>
    </Box>
  );
};
